import { type ReactNode, memo, useEffect, useMemo } from 'react';

import { Box, Button, Stack, Typography } from '@mui/material';
import { FileWarning, RefreshCw } from 'lucide-react';

import ActionBox from '@modules/_shared/components/report-editor/ActionBox';
import PreviewActions from '@modules/_shared/components/report-editor/preview/PreviewActions';
import { getReportPreviewVersion } from '@modules/_shared/components/report-editor/preview/reportPreviewVersion';
import {
  type PageId,
  useReportContext,
} from '@modules/_shared/components/report-editor/ReportEditorContext';
import { useReportEditorPerformance } from '@modules/_shared/components/report-editor/useReportEditorPerformance';
import useResponsiveIframeRenderer from '@modules/_shared/components/report-editor/useResponsiveIframeRenderer';
import { useTemplateRenderer } from '@modules/_shared/utils/template-renderer';
import { useLocale } from '@shared/hooks/locale';

type BasePreviewProps = {
  renderLoadingIndicator: () => ReactNode;
  onScrollToPageAvailable?: (scrollToPage: (pageId: PageId) => void) => void;
};

const iframeStyles = {
  width: '100%',
  zIndex: 0,
  border: 0,
  height: 0,
  flexGrow: 1,
} as const;

const containerStyles = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  position: 'relative',
} as const;

const PreviewError = ({
  detail,
  onRetry,
}: {
  detail: string | null;
  onRetry: () => void;
}) => {
  const { t } = useLocale();

  return (
    <Stack
      spacing={1.5}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <FileWarning size={32} />
      <Typography variant="body2" color="text.secondary">
        {t('The preview could not be loaded.')}
      </Typography>
      {detail != null && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontFamily: 'monospace',
            maxWidth: 520,
            px: 2,
            textAlign: 'center',
            overflowWrap: 'anywhere',
          }}
        >
          {detail}
        </Typography>
      )}
      <Button
        variant="outlined"
        size="small"
        startIcon={<RefreshCw size={16} />}
        onClick={onRetry}
      >
        {t('Retry')}
      </Button>
    </Stack>
  );
};

const BasePreview = memo(
  ({ renderLoadingIndicator, onScrollToPageAvailable }: BasePreviewProps) => {
    const {
      data,
      scrollToPageRef,
      iFrameRef,
      setActiveIframeRef,
      viewerPageId,
      setRenderFilename,
      previewOnly,
      setMarkup: setContextMarkup,
      documentId,
      documentType,
      previewVersion,
    } = useReportContext();

    const latestUpdate = useMemo(
      () => previewVersion ?? getReportPreviewVersion(data),
      [previewVersion, data],
    );

    const performanceTracker = useReportEditorPerformance({
      documentType,
      documentId,
    });

    const { markup, isLoading, hasError, errorDetail, retry, renderFilename } =
      useTemplateRenderer({
        key: latestUpdate,
        performanceTracker,
      });

    const activeIframeRef = useResponsiveIframeRenderer(
      iFrameRef.current,
      markup,
      viewerPageId,
      scrollToPageRef,
      true,
      performanceTracker,
    );

    useEffect(() => {
      setActiveIframeRef(activeIframeRef);
    }, [activeIframeRef, setActiveIframeRef]);

    useEffect(() => {
      setContextMarkup(markup);
    }, [markup, setContextMarkup]);

    useEffect(() => {
      renderFilename()
        .then(setRenderFilename)
        .catch(error => {
          console.error('Failed to resolve filename:', error);
          setRenderFilename(undefined);
        });
    }, [renderFilename, setRenderFilename]);

    useEffect(() => {
      if (onScrollToPageAvailable) {
        onScrollToPageAvailable(scrollToPageRef.current);
      }
    }, [onScrollToPageAvailable, scrollToPageRef]);

    return (
      <Box sx={containerStyles}>
        {hasError && markup == null ? (
          <PreviewError detail={errorDetail} onRetry={retry} />
        ) : (
          (isLoading || markup == null) && renderLoadingIndicator()
        )}
        {previewOnly ? <PreviewActions /> : <ActionBox />}
        <iframe
          ref={iFrameRef}
          title="Report Preview"
          style={iframeStyles}
          sandbox="allow-same-origin allow-scripts allow-modals"
        />
      </Box>
    );
  },
);

export default BasePreview;
